import React from 'react';
import styled from 'styled-components';
import Modal from '../../../Modal/Modal';
import { CloseIcon } from '../../../Icons';
import { H1, H5 } from '../../../elements/Text';
import { MarkdownWrapper } from '../common';
import { MultiLineInput } from '../../../elements/Input';
import {
  AssessmentTestStep,
  AssessmentTestStepAnswer,
} from '../../../../api/admin';
import dayjs from 'dayjs';

// Modal wrapper and structure
export const StepModalWrapper = styled(Modal)`
  padding: 0 64px 72px;
  width: 70vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.bgActive} transparent;
`;

export const StepModalContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

export const StepModalCloseButton = styled(CloseIcon)`
  padding: 0;
`;

export const StepModalHeaderRow = styled.div`
  display: flex;
  padding-top: 42px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  z-index: 100;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

export const StepModalColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StepModalTitle = styled(H1)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

export const StepModalLabel = styled.div`
  font-size: 16px;
  margin-top: 0;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.textPlaceholder};
`;

export const StepModalSectionTitle = styled(H5)`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.5px;
  margin-top: 64px;
`;

export const StepModalSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CompactTextArea = styled(MultiLineInput)`
  font-size: 13px;
  padding: 0;
  margin: 0;
  pointer-events: none;

  div {
    padding: 0;
  }

  textarea {
    font-size: 13px;
    line-height: 1.4;
    padding: 14px 16px;
  }
`;

export const AnswerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

export const Option = styled.div<{ isSelected: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  padding: 6px 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: 8px;
  transition: all 0.3s ease-in-out;
  font-size: 11px;
  font-weight: 500;

  ${({ isSelected, theme }) =>
    isSelected &&
    `
      border-color: ${theme.colors.primary};
      background-color: ${theme.colors.inputBackground};
    `}
`;

const CenteredOption = styled(Option)`
  flex: 1;
  text-align: center;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const InlineBlank = styled.span`
  display: inline-block;
  padding: 2px 12px;
  margin: 0 6px;
  border: 1px solid ${({ theme }) => theme.colors.inputBorderActive};
  color: ${({ theme }) => theme.colors.inputTextActive};
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 16px;
  min-width: 100px;
  text-align: center;
`;

const FillInTheGapWrapper = styled.div`
  border: 2px dashed ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  padding: 24px;
  background-color: transparent;
  line-height: 2;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const MatchingArrow = styled.div<{ hasConnection: boolean }>`
  align-self: center;
  color: ${({ theme, hasConnection }) =>
    hasConnection ? theme.colors.primary : theme.colors.textPlaceholder};
  font-size: 20px;
  font-weight: ${({ hasConnection }) => (hasConnection ? 'bold' : 'normal')};
  opacity: ${({ hasConnection }) => (hasConnection ? 1 : 0.3)};
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

const SkillLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2px;
`;

const QuestionText = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textRegular};
  margin-bottom: 4px;
`;

const OptionsRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

export const BasicPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${({ theme }) => `${theme.colors.textPrimary}1A`};
  border: 1px solid ${({ theme }) => `${theme.colors.textPrimary}1A`};
  color: ${({ theme }) => theme.colors.textPrimary};

  i {
    margin-right: 4px;
  }
`;

export const BadgesRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const TimeIcon = styled.i.attrs({ className: 'fa-regular fa-clock' })``;
export const TimeTakenPill = ({ timeTaken }: { timeTaken: number }) => {
  const formatDuration = (seconds: number) => {
    return dayjs()
      .startOf('day')
      .second(seconds)
      .format(seconds > 3600 ? 'HH:mm:ss' : 'mm:ss');
  };

  return (
    <BasicPill>
      <TimeIcon />
      Time taken: {formatDuration(timeTaken)}
    </BasicPill>
  );
};

// Reusable answer components
export function TextInputAnswer({ answer }: { answer: string }) {
  const [value, setValue] = React.useState('');
  React.useEffect(() => {
    setValue(answer);
  }, [answer]);
  return (
    <AnswerWrapper>
      <CompactTextArea
        value={value}
        maxRows={30}
        onChange={() => {}}
        placeholder="No answer"
      />
    </AnswerWrapper>
  );
}

export function MultipleRatingAnswer({
  answer,
  questions,
}: {
  answer: Array<{ id: string; label: string }>;
  questions: Array<{ id: string; content: string; options: string[] }>;
}) {
  // Extract skill name from markdown content (between ** markers)
  const extractSkillName = (content: string): string => {
    const match = content.match(/\*\*(.*?)\*\*/);
    return match ? match[1] : '';
  };

  // Clean up question text by removing the skill name and extra markdown
  const cleanQuestionText = (content: string): string => {
    return content
      .replace(/\*\*(.*?)\*\*\n\n/g, '')
      .replace(/\*/g, '')
      .trim();
  };

  return (
    <AnswerWrapper>
      {questions.map((question, index) => {
        const skillName = extractSkillName(question.content);
        const questionText = cleanQuestionText(question.content);
        const userAnswer = answer[index];

        return (
          <QuestionContainer key={question.id}>
            {skillName && <SkillLabel>{skillName}</SkillLabel>}
            {questionText && <QuestionText>{questionText}</QuestionText>}
            <OptionsRow>
              {question.options.map((option) => (
                <CenteredOption
                  key={option}
                  isSelected={userAnswer?.label === option}
                >
                  {option}
                </CenteredOption>
              ))}
            </OptionsRow>
          </QuestionContainer>
        );
      })}
    </AnswerWrapper>
  );
}

// Reusable modal header component
export function StepModalHeader({
  label,
  title,
  onClose,
}: {
  label: string;
  title: string;
  onClose: () => void;
}) {
  return (
    <StepModalHeaderRow>
      <StepModalColumn>
        <StepModalLabel>{label}</StepModalLabel>
        <StepModalTitle>{title}</StepModalTitle>
      </StepModalColumn>
      <StepModalCloseButton onClick={onClose} />
    </StepModalHeaderRow>
  );
}

export function MultipleChoiceAnswer({
  answer,
  options,
}: {
  answer: Array<{ id: string; content: string }>;
  options: Array<{ id: string; content: string }>;
}) {
  return (
    <AnswerWrapper>
      {options.map((option) => (
        <Option
          key={option.id}
          isSelected={answer.some((a) => a.id === option.id)}
        >
          <MarkdownWrapper md={option.content} />
        </Option>
      ))}
    </AnswerWrapper>
  );
}

export function RankingAnswer({
  answer,
  options,
}: {
  answer: Array<{ id: string; content: string }>;
  options: Array<{ id: string; content: string }>;
}) {
  return (
    <AnswerWrapper>
      {(answer.length === 0 ? options : answer).map((option, index) => (
        <Option key={option.id} isSelected={false}>
          <MarkdownWrapper md={`${index + 1}. ${option.content}`} />
        </Option>
      ))}
    </AnswerWrapper>
  );
}

export function MatchingAnswer({
  answer,
  leftItems,
  rightItems,
}: {
  answer: Array<{ leftId: string; rightId: string }>;
  leftItems: Array<{ id: string; content: string }>;
  rightItems: Array<{ id: string; content: string }>;
}) {
  if (answer.length === 0) {
    return (
      <AnswerWrapper>
        <Row style={{ gap: '32px', alignItems: 'flex-start' }}>
          <Column style={{ flex: 1, gap: '8px' }}>
            {leftItems.map((item) => (
              <Option key={item.id} isSelected={false}>
                <MarkdownWrapper md={item.content} />
              </Option>
            ))}
          </Column>
          <Column style={{ flex: 1, gap: '8px' }}>
            {rightItems.map((item) => (
              <Option key={item.id} isSelected={false}>
                <MarkdownWrapper md={item.content} />
              </Option>
            ))}
          </Column>
        </Row>
      </AnswerWrapper>
    );
  }

  const rightById = new Map(rightItems.map((i) => [i.id, i]));
  const leftToRight = new Map<string, string>();
  const matchedLeftIds = new Set<string>();
  const matchedRightIds = new Set<string>();

  answer.forEach((pair) => {
    leftToRight.set(pair.leftId, pair.rightId);
    matchedLeftIds.add(pair.leftId);
    matchedRightIds.add(pair.rightId);
  });

  const matchedRows: Array<{
    left: { id: string; content: string };
    right: { id: string; content: string };
    hasConnection: boolean;
  }> = [];

  const unmatchedRows: Array<{
    left?: { id: string; content: string };
    right?: { id: string; content: string };
    hasConnection: boolean;
  }> = [];

  leftItems.forEach((leftItem) => {
    if (matchedLeftIds.has(leftItem.id)) {
      const rightId = leftToRight.get(leftItem.id);
      const rightItem = rightId ? rightById.get(rightId) : undefined;
      if (rightItem) {
        matchedRows.push({
          left: leftItem,
          right: rightItem,
          hasConnection: true,
        });
      }
    }
  });

  leftItems.forEach((leftItem) => {
    if (!matchedLeftIds.has(leftItem.id)) {
      unmatchedRows.push({
        left: leftItem,
        right: undefined,
        hasConnection: false,
      });
    }
  });

  rightItems.forEach((rightItem) => {
    if (!matchedRightIds.has(rightItem.id)) {
      unmatchedRows.push({
        left: undefined,
        right: rightItem,
        hasConnection: false,
      });
    }
  });

  const allRows = [...matchedRows, ...unmatchedRows];

  return (
    <AnswerWrapper>
      {allRows.map((row, idx) => (
        <Row
          key={
            row.left?.id && row.right?.id
              ? `${row.left.id}-${row.right.id}`
              : row.left?.id
              ? `left-${row.left.id}`
              : `right-${row.right?.id ?? idx}`
          }
          style={{ gap: '16px', alignItems: 'stretch' }}
        >
          <Option
            isSelected={row.hasConnection}
            style={{ flex: 1, opacity: row.left ? 1 : 0.3 }}
          >
            <MarkdownWrapper md={row.left?.content ?? '—'} />
          </Option>
          <MatchingArrow hasConnection={row.hasConnection}>→</MatchingArrow>
          <Option
            isSelected={row.hasConnection}
            style={{ flex: 1, opacity: row.right ? 1 : 0.3 }}
          >
            <MarkdownWrapper md={row.right?.content ?? '—'} />
          </Option>
        </Row>
      ))}
    </AnswerWrapper>
  );
}

function splitByPlaceholders(
  text: string
): Array<{ type: 'text'; value: string } | { type: 'blank'; id: string }> {
  const result: Array<
    { type: 'text'; value: string } | { type: 'blank'; id: string }
  > = [];
  const regex = /\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const [full, id] = match;
    const idx = match.index;
    if (idx > lastIndex) {
      result.push({ type: 'text', value: text.slice(lastIndex, idx) });
    }
    result.push({ type: 'blank', id });
    lastIndex = idx + full.length;
  }
  if (lastIndex < text.length) {
    result.push({ type: 'text', value: text.slice(lastIndex) });
  }
  if (result.length === 0) {
    result.push({ type: 'text', value: text });
  }
  return result;
}

export function FillInTheGapAnswer({
  answer,
  section,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'fill-in-the-gap' }
  >['answer'];
  section: Extract<
    AssessmentTestStep['sections'][number],
    { type: 'fill-in-the-gap' }
  >;
}) {
  const answerById = new Map(answer.map((a) => [a.id, a.content]));
  const blankMetaById = new Map(
    section.blanks.map((b) => [
      b.id,
      { placeholder: b.placeholder, defaultContent: b.content },
    ])
  );

  const parts = splitByPlaceholders(section.template || '');

  return (
    <AnswerWrapper>
      <FillInTheGapWrapper>
        {parts.map((part, idx) => {
          if (part.type === 'text') {
            return <span key={`text-${idx}`}>{part.value}</span>;
          }
          const value =
            answerById.get(part.id) ||
            blankMetaById.get(part.id)?.defaultContent ||
            blankMetaById.get(part.id)?.placeholder ||
            '[No answer]';
          return (
            <InlineBlank key={`blank-${part.id}-${idx}`}>{value}</InlineBlank>
          );
        })}
      </FillInTheGapWrapper>
    </AnswerWrapper>
  );
}

const TextTemplateWrapper = styled.div`
  border: 2px dashed ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  padding: 24px;
  background-color: transparent;
  line-height: 1.8;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TemplateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TemplateBlankInput = styled(CompactTextArea)`
  width: 100%;
  margin: 0;

  div {
    padding: 0;
  }

  textarea {
    font-size: 13px;
    padding: 12px 16px;
    min-height: 60px;
    background-color: ${({ theme }) => theme.colors.inputBackground};
    width: 100%;
    overflow-y: hidden !important;
    resize: none;
  }
`;

function parseTemplateContent(
  template: string
): Array<{ type: 'text'; value: string } | { type: 'blank'; id: string }> {
  const result: Array<
    { type: 'text'; value: string } | { type: 'blank'; id: string }
  > = [];

  // Split by {{ id }} placeholder pattern
  const regex = /\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    const [full, id] = match;
    const idx = match.index;

    if (idx > lastIndex) {
      const textContent = template.slice(lastIndex, idx);
      result.push({ type: 'text', value: textContent });
    }

    result.push({ type: 'blank', id });
    lastIndex = idx + full.length;
  }

  if (lastIndex < template.length) {
    const textContent = template.slice(lastIndex);
    result.push({ type: 'text', value: textContent });
  }

  return result;
}

export function TextTemplateAnswer({
  answer,
  section,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'text-template' }
  >['answer'];
  section: Extract<
    AssessmentTestStep['sections'][number],
    { type: 'text-template' }
  >;
}) {
  const answerById = new Map(answer.map((a) => [a.id, a.content]));
  const blankMetaById = new Map(
    section.blanks.map((b) => [
      b.id,
      { placeholder: b.placeholder, defaultContent: b.content },
    ])
  );

  const templateContent = section.template || '';
  const parts = parseTemplateContent(templateContent);

  return (
    <AnswerWrapper>
      <TextTemplateWrapper>
        {parts.map((part, idx) => {
          if (part.type === 'text') {
            return (
              <TemplateSection key={`text-${idx}`}>
                <MarkdownWrapper md={part.value} />
              </TemplateSection>
            );
          }
          const value =
            answerById.get(part.id) ||
            blankMetaById.get(part.id)?.defaultContent ||
            blankMetaById.get(part.id)?.placeholder ||
            '';

          return (
            <TemplateSection key={`blank-${part.id}-${idx}`}>
              <TemplateBlankInput
                value={value}
                placeholder={
                  blankMetaById.get(part.id)?.placeholder ||
                  'No answer provided'
                }
                maxRows={20}
                onChange={() => {}}
              />
            </TemplateSection>
          );
        })}
      </TextTemplateWrapper>
    </AnswerWrapper>
  );
}

export function PythonPlaygroundAnswer({
  answer,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'py-playground' }
  >['answer'];
}) {
  return <MarkdownWrapper md={`\`\`\`python\n${answer.code}\n\`\`\``} />;
}

export function LLMPlaygroundAnswer({
  answer,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'llm-playground' }
  >['answer'];
}) {
  return <TextInputAnswer answer={answer.prompt} />;
}

export function PythonEditorAnswer({
  answer,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'py-editor' }
  >['answer'];
}) {
  return (
    <MarkdownWrapper
      // @ts-expect-error - code comes from simulation steps
      md={`\`\`\`python\n${answer?.code || answer || ''}\n\`\`\``}
    />
  );
}

export function LLMEditorAnswer({
  answer,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'llm-editor' }
  >['answer'];
}) {
  // @ts-expect-error - answer comes from simulations
  return <TextInputAnswer answer={answer?.prompt || answer || ''} />;
}

const MatrixItemCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MatrixItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const MatrixItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const MatrixCoordinates = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CoordinateChip = styled.div`
  font-size: 11px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
`;

const MatrixItemNote = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textRegular};
`;

export function MatrixRankingAnswer({
  answer,
  section,
}: {
  answer: Extract<
    AssessmentTestStepAnswer['sections'][number],
    { type: 'matrix-ranking' }
  >['answer'];
  section: Extract<
    AssessmentTestStep['sections'][number],
    { type: 'matrix-ranking' }
  >;
}) {
  if (!answer || answer.length === 0) {
    return <div style={{ opacity: 0.5 }}>No items placed</div>;
  }

  return (
    <AnswerWrapper>
      {answer.map((item, index) => (
        <MatrixItemCard key={`${item.item}-${index}`}>
          <MatrixItemHeader>
            <MatrixItemName>
              {section?.items?.find((i) => i.id === item.item)?.content}
            </MatrixItemName>
            <MatrixCoordinates>
              <CoordinateChip>X: {item.x}</CoordinateChip>
              <CoordinateChip>Y: {item.y}</CoordinateChip>
            </MatrixCoordinates>
          </MatrixItemHeader>
          {item.note && <MatrixItemNote>{item.note}</MatrixItemNote>}
        </MatrixItemCard>
      ))}
    </AnswerWrapper>
  );
}

// Helper to render answer based on section type
export function renderSectionAnswer(
  answer: AssessmentTestStepAnswer['sections'][number] | undefined,
  section: AssessmentTestStep['sections'][number]
) {
  if (section.type === 'text-input') {
    const answerString =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'text-input' }
        >
      )?.answer ?? '';
    return <TextInputAnswer answer={answerString} />;
  }

  if (section.type === 'multiple-choice') {
    const multipleChoiceAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'multiple-choice' }
        >
      )?.answer ?? [];
    return (
      <MultipleChoiceAnswer
        answer={multipleChoiceAnswer}
        options={section.options ?? []}
      />
    );
  }

  if (section.type === 'ranking') {
    const rankingAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'ranking' }
        >
      )?.answer ?? [];
    return (
      <RankingAnswer answer={rankingAnswer} options={section.options ?? []} />
    );
  }

  if (section.type === 'multiple-rating') {
    const multipleRatingAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'multiple-rating' }
        >
      )?.answer ?? [];
    return (
      <MultipleRatingAnswer
        answer={multipleRatingAnswer}
        questions={section.questions ?? []}
      />
    );
  }

  if (section.type === 'matching') {
    const matchingAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'matching' }
        >
      )?.answer ?? [];
    return (
      <MatchingAnswer
        answer={matchingAnswer}
        leftItems={section.leftItems ?? []}
        rightItems={section.rightItems ?? []}
      />
    );
  }

  if (section.type === 'fill-in-the-gap') {
    const fillInTheGapAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'fill-in-the-gap' }
        >
      )?.answer ?? [];
    return <FillInTheGapAnswer answer={fillInTheGapAnswer} section={section} />;
  }

  if (section.type === 'matrix-ranking') {
    const matrixRankingAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'matrix-ranking' }
        >
      )?.answer ?? [];
    return (
      <MatrixRankingAnswer answer={matrixRankingAnswer} section={section} />
    );
  }

  if (section.type === 'text-template') {
    const textTemplateAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'text-template' }
        >
      )?.answer ?? [];
    return <TextTemplateAnswer answer={textTemplateAnswer} section={section} />;
  }

  if (section.type === 'py-playground') {
    const pythonPlaygroundAnswer = (
      answer as Extract<
        AssessmentTestStepAnswer['sections'][number],
        { type: 'py-playground' }
      >
    )?.answer ?? { output: '', code: '' };
    return <PythonPlaygroundAnswer answer={pythonPlaygroundAnswer} />;
  }

  if (section.type === 'llm-playground') {
    const llmPlaygroundAnswer = (
      answer as Extract<
        AssessmentTestStepAnswer['sections'][number],
        { type: 'llm-playground' }
      >
    )?.answer ?? { output: '', prompt: '' };
    return <LLMPlaygroundAnswer answer={llmPlaygroundAnswer} />;
  }

  if (section.type === 'py-editor') {
    const pythonEditorAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'py-editor' }
        >
      )?.answer ?? '';
    return <PythonEditorAnswer answer={pythonEditorAnswer} />;
  }

  if (section.type === 'llm-editor') {
    const llmEditorAnswer =
      (
        answer as Extract<
          AssessmentTestStepAnswer['sections'][number],
          { type: 'llm-editor' }
        >
      )?.answer ?? '';
    return <LLMEditorAnswer answer={llmEditorAnswer} />;
  }

  return null;
}
